import { useState, useEffect } from "react";
import axios from "axios";
import { Barbell, Timer, Fire, TrendUp, Lightning, Trophy, ShareNetwork, Check, CalendarCheck, ArrowRight, Circle, Play } from "@phosphor-icons/react";
import { useWeightUnit } from "@/lib/units";
import WelcomePlanHero from "@/components/WelcomePlanHero";
import WeeklyRecapCard from "@/components/WeeklyRecapCard";

const StatCard = ({ icon: Icon, label, value, unit }) => (
  <div className="stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={18} className="text-[#E05A1D]" weight="bold" />
      <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A]">{label}</span>
    </div>
    <div className="flex items-baseline gap-1">
      <span className="text-3xl font-black text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
        {value}
      </span>
      {unit && <span className="text-sm text-[#A1A1AA]">{unit}</span>}
    </div>
  </div>
);

export default function Dashboard({ stats, workouts, api, profile, schedule, templates, onQuickLog, onStartSession, onOpenWorkout, onNavigate }) {
  const { fromKg, label: weightLabel } = useWeightUnit();
  const recentWorkouts = (workouts || []).slice(0, 5);
  const currentStreak = stats?.current_streak || 0;
  const bestStreak = stats?.best_streak || 0;
  const [prs, setPrs] = useState([]);
  const [streakCopied, setStreakCopied] = useState(false);
  const firstName = (profile?.name || "").trim().split(/\s+/)[0];

  // Today's plan from schedule + a 7-day strip (yesterday → +6 days)
  const fmtISO = (d) => d.toISOString().slice(0, 10);
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const entriesForDate = (d) =>
    (schedule || []).filter((s) => s.date === fmtISO(d));

  const findTpl = (entry) =>
    entry?.template_id ? (templates || []).find((t) => t.id === entry.template_id) : null;

  const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Build 7 days: yesterday, today, tomorrow + 4 more
  const stripDays = Array.from({ length: 7 }, (_, i) => {
    const offset = i - 1; // -1 = yesterday, 0 = today, 1 = tomorrow, ...
    const d = new Date(today); d.setDate(today.getDate() + offset);
    let label;
    if (offset === -1) label = "Yesterday";
    else if (offset === 0) label = "Today";
    else if (offset === 1) label = "Tomorrow";
    else label = DAY_NAMES[d.getDay()];
    return { label, date: d, entries: entriesForDate(d), isToday: offset === 0, isPast: offset < 0 };
  });

  const todayEntries = stripDays.find((s) => s.isToday)?.entries || [];
  const todayPrimary = todayEntries.find((s) => !s.completed) || todayEntries[0] || null;
  const todayTemplate = findTpl(todayPrimary);

  const handleQuickLogToday = () => {
    if (!todayPrimary) return;
    const payload = todayTemplate
      ? todayTemplate
      : { name: todayPrimary.workout_name, exercises: [], duration_minutes: 45 };
    onQuickLog?.(payload);
  };

  const handleStartToday = () => {
    if (!todayPrimary || !todayTemplate) return;
    onStartSession?.(todayTemplate, todayPrimary.id);
  };

  useEffect(() => {
    if (api) {
      axios.get(`${api}/personal-records`).then(res => setPrs((res.data || []).slice(0, 3))).catch(() => {});
    }
  }, [api]);

  const shareStreak = async () => {
    const text = `I'm on a ${currentStreak}-day workout streak on UpTop!${bestStreak > currentStreak ? ` My best: ${bestStreak} days.` : " That's my best ever!"}\n\n#UpTop #WorkoutStreak #Consistency`;
    if (navigator.share) {
      try { await navigator.share({ title: "UpTop Streak", text }); return; } catch (e) { /* fallback */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      setStreakCopied(true);
      setTimeout(() => setStreakCopied(false), 2000);
    } catch (e) { /* clipboard not available */ }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h2
          className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="dashboard-title"
        >
          {firstName ? (
            <>Welcome back, <span className="text-[#E05A1D]" data-testid="dashboard-user-name">{firstName}</span></>
          ) : (
            "Summary"
          )}
        </h2>
        <p className="text-sm text-[#71717A] mt-1">Your fitness overview at a glance</p>
      </div>

      {/* Magic-moment welcome card — only renders if Onboarding just stashed
          a freshly-generated AI plan in localStorage. Self-clears on
          dismiss/open. */}
      <WelcomePlanHero onNavigate={onNavigate} />

      {/* Weekly recap — opt-in via profile.dashboard_recap_optin (default on);
          only renders when last week had at least one workout. */}
      <WeeklyRecapCard api={api} profile={profile} />

      {/* Streak Badge */}
      <div
        className={`border p-5 flex items-center gap-5 transition-all ${
          currentStreak > 0
            ? "bg-gradient-to-r from-[#E05A1D]/10 to-transparent border-[#E05A1D]/30"
            : "bg-[#161616] border-[#27272A]"
        }`}
        data-testid="streak-badge"
      >
        <div className={`w-16 h-16 flex items-center justify-center ${
          currentStreak > 0 ? "bg-[#E05A1D]/20 text-[#E05A1D]" : "bg-[#27272A] text-[#71717A]"
        }`}>
          <Lightning size={32} weight={currentStreak > 0 ? "fill" : "regular"} />
        </div>
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <span
              className={`text-4xl font-black ${currentStreak > 0 ? "text-[#E05A1D]" : "text-[#71717A]"}`}
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="streak-count"
            >
              {currentStreak}
            </span>
            <span className="text-sm text-[#A1A1AA] font-bold uppercase tracking-wider">
              day streak
            </span>
          </div>
          <p className="text-xs text-[#71717A] mt-1">
            {currentStreak > 0
              ? currentStreak >= bestStreak
                ? "You're on your best streak! Keep going!"
                : `Best streak: ${bestStreak} days — keep pushing!`
              : "Log a workout today to start your streak!"
            }
          </p>
        </div>
        {bestStreak > 0 && bestStreak !== currentStreak && (
          <div className="text-right">
            <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A]">Best</p>
            <p
              className="text-2xl font-black text-[#A1A1AA]"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="best-streak"
            >
              {bestStreak}
            </p>
            <p className="text-xs text-[#71717A]">days</p>
          </div>
        )}
        {currentStreak > 0 && (
          <button
            onClick={shareStreak}
            className="text-[#71717A] hover:text-[#E05A1D] transition-colors p-2 border border-[#27272A] hover:border-[#E05A1D]"
            data-testid="share-streak-btn"
          >
            {streakCopied ? <Check size={16} weight="bold" className="text-[#22C55E]" /> : <ShareNetwork size={16} />}
          </button>
        )}
      </div>

      {/* Today's Plan — 7-day strip (horizontal scroll on mobile) */}
      <div
        className="flex overflow-x-auto gap-3 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-7 snap-x snap-mandatory scroll-smooth no-scrollbar"
        data-testid="todays-plan-strip"
      >
        {stripDays.map(({ label, date, entries, isToday, isPast }) => {
          const primary = entries.find((s) => !s.completed) || entries[0] || null;
          const tpl = findTpl(primary);
          const dateStr = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
          return (
            <div
              key={label}
              className={`min-w-[64%] sm:min-w-[42%] md:min-w-0 flex-shrink-0 snap-start border p-3 transition-all flex flex-col ${
                isToday
                  ? "bg-gradient-to-br from-[#E05A1D]/15 to-transparent border-[#E05A1D]/40"
                  : isPast
                    ? "bg-[#0F0F0F] border-[#27272A] opacity-80"
                    : "bg-[#161616] border-[#27272A]"
              }`}
              data-testid={`day-card-${label.toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className={`text-[10px] font-bold tracking-[0.1em] uppercase ${isToday ? "text-[#E05A1D]" : "text-[#71717A]"}`}>{label}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#71717A]">{dateStr}</p>
                </div>
                {primary && (
                  primary.completed
                    ? <Check size={14} weight="bold" className="text-[#22C55E]" />
                    : isToday
                      ? <CalendarCheck size={14} weight="fill" className="text-[#E05A1D]" />
                      : <Circle size={12} className="text-[#71717A]" />
                )}
              </div>
              <div className="flex-1 min-h-[44px]">
                {primary ? (
                  <>
                    <p className={`text-sm font-bold truncate ${
                      primary.completed ? "text-[#22C55E] line-through" : "text-white"
                    }`} data-testid={`day-workout-${label.toLowerCase()}`} title={primary.workout_name}>
                      {primary.workout_name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {tpl?.category && (
                        <span className="text-[9px] font-bold uppercase tracking-wider text-[#E05A1D]">{tpl.category}</span>
                      )}
                      {entries.length > 1 && (
                        <span className="text-[9px] uppercase tracking-wider text-[#A1A1AA]">+{entries.length - 1}</span>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-[#71717A]">{isToday ? "Nothing scheduled" : isPast ? "—" : "Open"}</p>
                )}
              </div>
              {isToday && (
                primary && !primary.completed ? (
                  <div className="mt-2 space-y-1.5" data-testid="today-actions">
                    {todayTemplate && (
                      <button
                        onClick={handleStartToday}
                        className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold uppercase tracking-wide py-2 text-[11px] flex items-center justify-center gap-1.5 transition-colors"
                        data-testid="todays-plan-start"
                      >
                        <Play size={12} weight="fill" /> Start
                      </button>
                    )}
                    <button
                      onClick={handleQuickLogToday}
                      className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-2 text-[11px] flex items-center justify-center gap-1.5 transition-colors"
                      data-testid="todays-plan-quick-log"
                    >
                      <Lightning size={12} weight="fill" /> Quick Log
                    </button>
                  </div>
                ) : !primary ? (
                  <button
                    onClick={() => onNavigate?.("calendar")}
                    className="mt-2 w-full bg-transparent border border-[#27272A] hover:border-[#E05A1D] text-[#A1A1AA] hover:text-[#E05A1D] font-bold uppercase tracking-wide py-2 text-[11px] flex items-center justify-center gap-1.5 transition-all"
                    data-testid="todays-plan-open-schedule"
                  >
                    Plan it <ArrowRight size={12} weight="bold" />
                  </button>
                ) : null
              )}
            </div>
          );
        })}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
        <StatCard icon={Barbell} label="Total Workouts" value={stats?.total_workouts || 0} />
        <StatCard icon={Fire} label="Total Volume" value={fromKg(stats?.total_volume || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} unit={weightLabel} />
        <StatCard icon={Timer} label="Avg Duration" value={stats?.avg_duration || 0} unit="min" />
        <StatCard icon={TrendUp} label="Total Time" value={stats?.total_duration || 0} unit="min" />
      </div>

      {/* Recent Workouts */}
      <div className="bg-[#161616] border border-[#27272A] p-5" data-testid="recent-workouts">
          <h3
            className="text-lg font-semibold tracking-tight uppercase text-white mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Recent Workouts
          </h3>
          {recentWorkouts.length > 0 ? (
            <div className="space-y-3">
              {recentWorkouts.map((w) => (
                <button
                  key={w.id}
                  onClick={() => onOpenWorkout?.(w)}
                  className="w-full text-left border border-[#27272A] p-3 hover:border-[#E05A1D]/40 transition-colors cursor-pointer"
                  data-testid={`recent-workout-${w.id}`}
                >
                  <p className="text-sm font-semibold text-white">{w.name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-[#71717A]">{w.date}</span>
                    <span className="text-xs text-[#E05A1D]">{w.exercises?.length || 0} exercises</span>
                    {w.duration_minutes > 0 && (
                      <span className="text-xs text-[#A1A1AA]">{w.duration_minutes}min</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#71717A]">No workouts logged yet</p>
          )}
      </div>

      {/* Top Exercises */}
      {stats?.top_exercises?.length > 0 && (
        <div className="bg-[#161616] border border-[#27272A] p-5" data-testid="top-exercises">
          <h3
            className="text-lg font-semibold tracking-tight uppercase text-white mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Top Exercises
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {stats.top_exercises.map((ex, i) => (
              <div key={i} className="border border-[#27272A] p-3 flex items-center justify-between">
                <span className="text-sm font-medium text-white">{ex.name}</span>
                <span className="text-xs font-bold text-[#E05A1D]">{ex.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top PRs */}
      {prs.length > 0 && (
        <div className="bg-[#161616] border border-[#E05A1D]/20 p-5 pr-glow" data-testid="dashboard-prs">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={18} weight="fill" className="text-[#E05A1D]" />
            <h3
              className="text-lg font-semibold tracking-tight uppercase text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Personal Records
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {prs.map((pr, i) => (
              <div key={pr.id} className="border border-[#27272A] p-4 flex items-center justify-between bg-[#0F0F0F]">
                <div>
                  <p className="text-sm font-semibold text-white">{pr.exercise_display}</p>
                  <p className="text-xs text-[#71717A] mt-0.5">{pr.date}</p>
                </div>
                <div className="text-right">
                  <p
                    className={`text-2xl font-black ${i === 0 ? "text-[#E05A1D]" : "text-white"}`}
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    {fromKg(pr.weight).toFixed(1)}
                  </p>
                  <p className="text-xs text-[#71717A]">{weightLabel}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
